import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Search, Upload, Home, ShieldCheck, TrendingUp, Send,
  CheckCircle2, XCircle, ArrowRightCircle, Trash2, RefreshCw, MessageSquareReply,
  BarChart3, Wand2, MapPinned, Pencil, Eye,
} from "lucide-react";
import {
  prospectStats, listProspects, prospectSegments, importProspects, discoverProspects,
  residentialProfile, listResidentialProfiles, qualifyProspect, draftOutreach,
  approvalQueue, approveProspect, rejectProspect, sendProspectOutreach,
  logProspectResponse, convertProspect, deleteProspect,
  qualifyAllNew, discoverSweep, prospectRoi, editProspectOutreach, previewProspectOutreach,
} from "@/lib/dashboardApi";

const NAVY = "#12355B";
const STATUS_STYLE = {
  new: "bg-slate-100 text-slate-600", qualified: "bg-blue-100 text-blue-700",
  outreach_ready: "bg-amber-100 text-amber-700", approved: "bg-indigo-100 text-indigo-700",
  contacted: "bg-cyan-100 text-cyan-700", responded: "bg-emerald-100 text-emerald-700",
  converted: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-600",
};
const PRIORITY_STYLE = { high: "bg-red-500", medium: "bg-amber-500", low: "bg-slate-400" };
const TABS = [
  { key: "pipeline", label: "Pipeline", icon: TrendingUp },
  { key: "discover", label: "Discover (B2B)", icon: Search },
  { key: "import", label: "Import / Paste", icon: Upload },
  { key: "residential", label: "Residential", icon: Home },
  { key: "approvals", label: "Approvals", icon: ShieldCheck },
  { key: "roi", label: "ROI Center", icon: BarChart3 },
];

export default function ProspectingEngine() {
  const [tab, setTab] = useState("pipeline");
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(async () => {
    try { setStats(await prospectStats()); } catch (e) { console.error("prospectStats load failed:", e); }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  return (
    <div data-testid="prospecting-engine">
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-launch" />
          <h2 className="text-xl font-bold" style={{ color: NAVY }}>New Business Engine</h2>
        </div>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          AI finds & qualifies opportunities, scores them, explains the fit, and drafts outreach — but
          nothing goes out until <span className="font-semibold">you approve it</span>. Every prospect
          carries a source so you can trace Source → Contact → Lead → Sold → Revenue.
        </p>
      </div>

      <StatsRow stats={stats} />

      <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-5">
        {TABS.map((t) => {
          const Icon = t.icon; const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`prospect-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${active ? "border-launch text-navy" : "border-transparent text-slate-500 hover:text-navy"}`}>
              <Icon className="h-4 w-4" /> {t.label}
              {t.key === "approvals" && stats?.awaiting_approval > 0 &&
                <span className="ml-1 text-[10px] bg-amber-500 text-white rounded-full px-1.5 py-0.5">{stats.awaiting_approval}</span>}
            </button>
          );
        })}
      </div>

      {tab === "pipeline" && <Pipeline onChange={loadStats} />}
      {tab === "discover" && <Discover onDone={() => { loadStats(); }} />}
      {tab === "import" && <ImportTab onDone={() => { loadStats(); }} />}
      {tab === "residential" && <Residential />}
      {tab === "approvals" && <Approvals onChange={loadStats} />}
      {tab === "roi" && <RoiCenter />}
    </div>
  );
}

const StatsRow = ({ stats }) => {
  const f = stats?.funnel || {};
  const cards = [
    { label: "Total Prospects", value: stats?.total ?? "—" },
    { label: "Qualified", value: f.qualified ?? 0 },
    { label: "Awaiting Approval", value: stats?.awaiting_approval ?? 0 },
    { label: "Outreach Sent", value: stats?.sent ?? 0 },
    { label: "Responded", value: f.responded ?? 0 },
    { label: "Converted → Leads", value: f.converted ?? 0 },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="prospect-stats">
      {cards.map((c) => (
        <div key={c.label} className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-2xl font-bold" style={{ color: NAVY }}>{c.value}</p>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mt-1">{c.label}</p>
        </div>
      ))}
    </div>
  );
};

function ProspectCard({ p, onChange, showDraft }) {
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(false);
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); if (msg) toast.success(msg); onChange && onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };
  const Spin = ({ k }) => busy === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null;
  const btn = "inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-colors disabled:opacity-50";

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`prospect-card-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-navy text-sm truncate">{p.business_name || p.contact_name || (p.segment ? `${p.segment} (unnamed listing)` : "Unnamed")}</p>
            <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${STATUS_STYLE[p.status] || "bg-slate-100"}`}>{p.status.replace("_", " ")}</span>
            {p.segment && <span className="text-[10px] font-medium text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{p.segment}</span>}
            <span className="text-[10px] font-medium text-slate-400 bg-slate-50 rounded px-1.5 py-0.5 border border-slate-200">{p.source.replace("_", " ")}</span>
            {!p.verified && <span className="text-[10px] font-semibold text-amber-600">unverified</span>}
            {p.sent_at && <span className="text-[10px] font-bold text-white bg-emerald-600 rounded px-1.5 py-0.5" data-testid={`prospect-sent-badge-${p.id}`}>✓ SENT</span>}
            {p.is_test && <span className="text-[10px] font-bold text-white bg-purple-500 rounded px-1.5 py-0.5" data-testid="prospect-test-badge">TEST</span>}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {[p.contact_name, p.phone, p.email, [p.city, p.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ") || "No contact details yet"}
            {p.website && <> · <a href={p.website} target="_blank" rel="noreferrer" className="text-cool underline">site</a></>}
          </p>
        </div>
        {p.ai_score != null && (
          <div className="text-right shrink-0">
            <div className="flex items-center gap-1.5 justify-end">
              <span className={`h-2 w-2 rounded-full ${PRIORITY_STYLE[p.ai_priority] || "bg-slate-300"}`} />
              <span className="text-lg font-bold text-navy">{p.ai_score}</span>
            </div>
            <p className="text-[10px] text-slate-400 uppercase">{p.ai_priority} priority</p>
          </div>
        )}
      </div>

      {(p.ai_rationale || (p.ai_fit_reasons || []).length > 0) && (
        <div className="mt-3">
          <button onClick={() => setOpen(!open)} className="text-xs font-semibold text-cool" data-testid={`prospect-why-${p.id}`}>
            {open ? "Hide" : "Why this prospect?"}
          </button>
          {open && (
            <div className="mt-2 bg-slate-50 rounded-lg p-3 text-xs text-slate-600 space-y-2">
              {p.ai_rationale && <p>{p.ai_rationale}</p>}
              {(p.ai_fit_reasons || []).length > 0 && (
                <ul className="list-disc pl-4 space-y-0.5">{p.ai_fit_reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
              )}
              {(p.ai_recommended_services || []).length > 0 && (
                <p className="text-slate-500"><span className="font-semibold">Recommend:</span> {p.ai_recommended_services.join(", ")}</p>
              )}
              {p.ai_estimated_value && <p className="text-slate-500"><span className="font-semibold">Est. opportunity:</span> {p.ai_estimated_value}</p>}
              {p.ai_outreach_angle && <p className="text-slate-500"><span className="font-semibold">Angle:</span> {p.ai_outreach_angle}</p>}
              {p.ai_tier_used && <p className="text-[10px] text-slate-400">Models: {p.ai_tier_used}</p>}
            </div>
          )}
        </div>
      )}

      {showDraft && p.outreach && (        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs" data-testid={`prospect-draft-${p.id}`}>
          <p className="font-semibold text-amber-800 uppercase text-[10px] tracking-wide">Drafted {p.outreach.channel}</p>
          {p.outreach.subject && <p className="font-semibold text-slate-700 mt-1">Subject: {p.outreach.subject}</p>}
          <p className="text-slate-600 mt-1 whitespace-pre-line">{p.outreach.body}</p>
        </div>
      )}

      {p.sent_at && (
        <div className="mt-3 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid={`prospect-sent-info-${p.id}`}>
          <Send className="h-3.5 w-3.5" />
          <span>Outreach sent {new Date(p.sent_at).toLocaleDateString()} {p.send_result?.reason ? `· ${p.send_result.reason}` : ""}</span>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {["new"].includes(p.status) && (
          <button disabled={busy} onClick={() => run("q", () => qualifyProspect(p.id), "Qualified")} className={`${btn} border-blue-300 text-blue-700 hover:bg-blue-50`} data-testid={`prospect-qualify-${p.id}`}><Spin k="q" /><Sparkles className="h-3.5 w-3.5" /> Qualify</button>
        )}
        {["new", "qualified"].includes(p.status) && (
          <button disabled={busy} onClick={() => run("d", () => draftOutreach(p.id, "email"), "Outreach drafted")} className={`${btn} border-amber-300 text-amber-700 hover:bg-amber-50`} data-testid={`prospect-draft-btn-${p.id}`}><Spin k="d" /><MessageSquareReply className="h-3.5 w-3.5" /> Draft outreach</button>
        )}
        {p.status === "responded" && (
          <button disabled={busy} onClick={() => run("c", () => convertProspect(p.id), "Converted to a lead")} className={`${btn} border-green-400 text-green-700 hover:bg-green-50`} data-testid={`prospect-convert-${p.id}`}><Spin k="c" /><ArrowRightCircle className="h-3.5 w-3.5" /> Convert to lead</button>
        )}
        {["contacted", "approved"].includes(p.status) && (
          <button disabled={busy} onClick={() => run("r", () => logProspectResponse(p.id), "Marked responded")} className={`${btn} border-emerald-300 text-emerald-700 hover:bg-emerald-50`} data-testid={`prospect-respond-${p.id}`}><Spin k="r" /><MessageSquareReply className="h-3.5 w-3.5" /> Log response</button>
        )}
        {!["converted", "rejected"].includes(p.status) && (
          <button disabled={busy} onClick={() => run("x", () => rejectProspect(p.id))} className={`${btn} border-slate-300 text-slate-500 hover:bg-slate-50`} data-testid={`prospect-reject-${p.id}`}><XCircle className="h-3.5 w-3.5" /> Reject</button>
        )}
        <button disabled={busy} onClick={() => run("del", () => deleteProspect(p.id))} className={`${btn} border-slate-200 text-slate-400 hover:text-red-500`} data-testid={`prospect-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}

function Pipeline({ onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [isTest, setIsTest] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await listProspects({ status, q, ...(isTest ? { is_test: isTest } : {}) })); } catch { toast.error("Could not load prospects"); }
    finally { setLoading(false); }
  }, [status, q, isTest]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChange && onChange(); };
  const [qualBusy, setQualBusy] = useState(false);
  const [qualMsg, setQualMsg] = useState("");
  const qualifyAll = async () => {
    setQualBusy(true);
    try {
      const r = await qualifyAllNew();
      toast.success(`Qualified ${r.qualified} (${r.high_priority} high priority)`);
      setQualMsg(r.remaining ? `${r.remaining} new prospects still to qualify — click again to continue` : "All new prospects qualified.");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Qualify failed"); }
    finally { setQualBusy(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <div className="relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-2.5" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email" className="pl-8 pr-3 py-2 text-sm border border-slate-300 rounded-lg" data-testid="pipeline-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-2 text-sm" data-testid="pipeline-status-filter">
          <option value="">All statuses</option>
          {["new", "qualified", "outreach_ready", "approved", "contacted", "responded", "converted", "rejected"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
        <select value={isTest} onChange={(e) => setIsTest(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-2 text-sm" data-testid="pipeline-test-filter">
          <option value="">Real + Test</option>
          <option value="false">Real only</option>
          <option value="true">Test only</option>
        </select>
        <button onClick={refresh} className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy px-3 py-2 border border-slate-300 rounded-lg" data-testid="pipeline-refresh"><RefreshCw className="h-4 w-4" /> Refresh</button>
        <button onClick={qualifyAll} disabled={qualBusy} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-launch px-3 py-2 rounded-lg disabled:opacity-50" data-testid="qualify-all-btn">{qualBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Qualify All New</button>
      </div>
      {qualMsg && <p className="text-xs text-slate-500 mb-3" data-testid="qualify-all-msg">{qualMsg}</p>}
      {loading ? <Loader2 className="h-6 w-6 animate-spin text-navy" /> :
        rows.length === 0 ? <Empty text="No prospects yet. Use Discover, Import, or Residential to add opportunities." /> :
          <div className="grid md:grid-cols-2 gap-3">{rows.map((p) => <ProspectCard key={p.id} p={p} onChange={refresh} />)}</div>}
    </div>
  );
}

function Discover({ onDone }) {
  const [segments, setSegments] = useState([]);
  const [segment, setSegment] = useState("Realtor");
  const [location, setLocation] = useState("Melbourne, FL");
  const [limit, setLimit] = useState(10);
  const [isTest, setIsTest] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { prospectSegments().then((d) => setSegments(d.b2b || [])).catch(() => {}); }, []);
  const go = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await discoverProspects({ segment, location, limit: Number(limit), is_test: isTest });
      setResult(r);
      if (r.created > 0) toast.success(`Found ${r.created} new prospect(s)`);
      else toast.message(r.reason || "No new prospects found");
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Discovery failed"); }
    finally { setBusy(false); }
  };
  const sweep = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await discoverSweep({ segment, start_city: location, target: 15, per_city_limit: 8, is_test: isTest });
      setResult({ status: "OK", created: r.created, query: `${segment} across ${r.cities_swept} nearby cities`, reason: "" });
      if (r.created > 0) toast.success(`Swept ${r.cities_swept} cities — found ${r.created} new prospect(s)`);
      else toast.message("No new prospects across nearby cities");
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sweep failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <p className="text-sm text-slate-600 mb-3">Find real local businesses from public map data (OpenStreetMap) — geo-accurate, no fabricated contacts.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {segments.map((s) => (
            <button key={s} onClick={() => setSegment(s)} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${segment === s ? "bg-navy text-white border-navy" : "border-slate-300 text-slate-600"}`} data-testid={`discover-seg-${s}`}>{s}</button>
          ))}
        </div>
        <div className="grid sm:grid-cols-3 gap-2">
          <input value={segment} onChange={(e) => setSegment(e.target.value)} placeholder="Business type" className="border border-slate-300 rounded-lg px-3 py-2 text-sm sm:col-span-1" data-testid="discover-segment" />
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, ST" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="discover-location" />
          <input type="number" min="1" max="20" value={limit} onChange={(e) => setLimit(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="discover-limit" />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600 mt-3" data-testid="discover-test-label">
          <input type="checkbox" checked={isTest} onChange={(e) => setIsTest(e.target.checked)} data-testid="discover-test" />
          Mark as TEST records (kept out of all outbound until unchecked)
        </label>
        <button disabled={busy} onClick={go} className="mt-3 inline-flex items-center gap-2 bg-navy text-white text-sm font-bold px-4 py-2 rounded-lg disabled:opacity-50" data-testid="discover-run">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Discover prospects
        </button>
        <button disabled={busy} onClick={sweep} className="mt-3 ml-2 inline-flex items-center gap-2 border border-navy text-navy text-sm font-bold px-4 py-2 rounded-lg disabled:opacity-50" data-testid="discover-sweep-run">
          <MapPinned className="h-4 w-4" /> Sweep nearby cities
        </button>
      </div>
      {result && (
        <p className="mt-3 text-sm text-slate-600" data-testid="discover-result">
          {result.status === "OK" ? `Added ${result.created} new prospect(s) for “${result.query}”. Open the Pipeline tab to qualify them.` : (result.reason || "No results")}
        </p>
      )}
    </div>
  );
}

function ImportTab({ onDone }) {
  const [text, setText] = useState("");
  const [segment, setSegment] = useState("");
  const [type, setType] = useState("b2b");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!text.trim()) { toast.error("Paste a list first"); return; }
    setBusy(true);
    try {
      const r = await importProspects({ text, segment, prospect_type: type, source_detail: "Manual import" });
      toast.success(`Imported ${r.created} prospect(s)`);
      setText(""); onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl bg-white rounded-xl border border-slate-200 p-5">
      <p className="text-sm text-slate-600 mb-3">Paste a CSV (with a header row like <code className="bg-slate-100 px-1 rounded">business_name,email,phone,city,state</code>) or a messy list — AI will structure freeform text automatically.</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} placeholder="business_name,contact_name,email,phone,city,state,segment&#10;Sunshine Realty,Maria Lopez,maria@sunshine.com,321-555-0100,Melbourne,FL,Realtor" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" data-testid="import-text" />
      <div className="flex flex-wrap gap-2 mt-2">
        <select value={type} onChange={(e) => setType(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-2 text-sm" data-testid="import-type">
          <option value="b2b">B2B</option><option value="residential">Residential</option>
        </select>
        <input value={segment} onChange={(e) => setSegment(e.target.value)} placeholder="Default segment (optional)" className="border border-slate-300 rounded-lg px-3 py-2 text-sm flex-1" data-testid="import-segment" />
        <button disabled={busy} onClick={go} className="inline-flex items-center gap-2 bg-navy text-white text-sm font-bold px-4 py-2 rounded-lg disabled:opacity-50" data-testid="import-run">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import
        </button>
      </div>
    </div>
  );
}

function Residential() {
  const [location, setLocation] = useState("Melbourne, FL");
  const [criteria, setCriteria] = useState("");
  const [busy, setBusy] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const load = useCallback(async () => { try { setProfiles(await listResidentialProfiles()); } catch (e) { console.error("residential profiles load failed:", e); } }, []);
  useEffect(() => { load(); }, [load]);
  const go = async () => {
    setBusy(true);
    try { await residentialProfile({ location, criteria }); toast.success("Targeting plan generated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <p className="text-sm text-slate-600 mb-3">AI builds a residential targeting plan — neighborhoods, household signals, weather triggers and campaign angles. No personal contacts, just where & how to win homeowner business.</p>
        <div className="flex flex-wrap gap-2">
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, ST" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="resi-location" />
          <input value={criteria} onChange={(e) => setCriteria(e.target.value)} placeholder="Notes (e.g. 15+ yr systems, coastal homes)" className="border border-slate-300 rounded-lg px-3 py-2 text-sm flex-1" data-testid="resi-criteria" />
          <button disabled={busy} onClick={go} className="inline-flex items-center gap-2 bg-navy text-white text-sm font-bold px-4 py-2 rounded-lg disabled:opacity-50" data-testid="resi-run">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Home className="h-4 w-4" />} Generate plan
          </button>
        </div>
      </div>
      <div className="mt-4 space-y-4">
        {profiles.map((pf) => <ResidentialPlan key={pf.id} pf={pf} />)}
      </div>
    </div>
  );
}

const ResidentialPlan = ({ pf }) => {
  const plan = pf.plan || {};
  const Section = ({ title, items, render }) => (items || []).length > 0 && (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">{title}</p>
      <ul className="space-y-1 text-sm text-slate-600">{items.map((it, i) => <li key={i}>{render(it)}</li>)}</ul>
    </div>
  );
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`resi-plan-${pf.id}`}>
      <p className="font-bold text-navy">{pf.location}{pf.criteria ? ` · ${pf.criteria}` : ""}</p>
      <div className="grid md:grid-cols-2 gap-4 mt-3">
        <Section title="Target Neighborhoods" items={plan.target_neighborhoods} render={(n) => <><span className="font-semibold text-navy">{n.name}</span>{n.why ? ` — ${n.why}` : ""}</>} />
        <Section title="Campaign Angles" items={plan.campaign_angles} render={(a) => <><span className="font-semibold text-navy">{a.headline}</span>{a.message ? ` — ${a.message}` : ""}{a.channel ? ` (${a.channel})` : ""}</>} />
        <Section title="Household Signals" items={plan.household_profile} render={(x) => x} />
        <Section title="Weather Triggers" items={plan.weather_triggers} render={(x) => x} />
        <Section title="Aging System Signals" items={plan.aging_system_signals} render={(x) => x} />
        <Section title="Recommended Offers" items={plan.recommended_offers} render={(x) => x} />
      </div>
    </div>
  );
};

function Approvals({ onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await approvalQueue()); } catch { toast.error("Could not load queue"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, key, fn, msg) => {
    setBusy(id + key);
    try { const r = await fn(); toast.success(typeof msg === "function" ? msg(r) : msg); load(); onChange && onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };
  if (loading) return <Loader2 className="h-6 w-6 animate-spin text-navy" />;
  if (rows.length === 0) return <Empty text="Nothing awaiting approval. Draft outreach on a prospect to send it here." />;
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">Review AI-drafted outreach. <span className="font-semibold">Approve</span> stages it; <span className="font-semibold">Send</span> emails the prospect. Nothing goes out automatically.</p>
      {rows.map((p) => (
        <ApprovalCard key={p.id} p={p} busy={busy} act={act} onReload={load} />
      ))}
    </div>
  );
}

function ApprovalCard({ p, busy, act, onReload }) {
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(p.outreach?.subject || "");
  const [message, setMessage] = useState(p.outreach?.message ?? p.outreach?.body ?? "");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [ploading, setPloading] = useState(false);
  const isEmail = (p.outreach?.channel || "email") === "email";
  const openPreview = async () => {
    setPloading(true);
    try { setPreview(await previewProspectOutreach(p.id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load preview"); }
    finally { setPloading(false); }
  };
  const reset = () => { setSubject(p.outreach?.subject || ""); setMessage(p.outreach?.message ?? p.outreach?.body ?? ""); };
  const save = async () => {
    if (!message.trim()) { toast.error("Message cannot be empty"); return; }
    setSaving(true);
    try { await editProspectOutreach(p.id, { subject, message }); toast.success("Draft updated"); setEditing(false); onReload && onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`approval-card-${p.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="font-semibold text-navy text-sm">{p.business_name || p.contact_name || (p.segment ? `${p.segment} (unnamed listing)` : "Unnamed")} <span className="text-slate-400 font-normal">· {p.segment}</span> {p.ai_score != null && <span className="text-xs text-slate-500">(score {p.ai_score})</span>}</p>
        <span className="text-xs text-slate-500">{p.email || "no email"} · {p.outreach?.channel}</span>
      </div>

      {editing ? (
        <div className="mt-2 space-y-2" data-testid={`approval-edit-${p.id}`}>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-launch/40" data-testid={`edit-subject-${p.id}`} />
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={7} className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-launch/40" data-testid={`edit-message-${p.id}`} />
          {isEmail && <p className="text-[11px] text-slate-400">You're editing your personal message only. Aquila's services block and Todd's signature (Lifetime Warranty + no-fees) are added to every email automatically.</p>}
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-green-400 text-green-700 hover:bg-green-50 disabled:opacity-50" data-testid={`edit-save-${p.id}`}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Save changes
            </button>
            <button onClick={() => { reset(); setEditing(false); }} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-300 text-slate-500 hover:bg-slate-50" data-testid={`edit-cancel-${p.id}`}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-2 bg-slate-50 rounded-lg p-3 text-sm">
            {p.outreach?.subject && <p className="font-semibold text-slate-700">Subject: {p.outreach.subject}</p>}
            <p className="text-slate-600 mt-1 whitespace-pre-line">{p.outreach?.body}</p>
          </div>
          {p.status === "approved" && (
            <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-green-700" data-testid={`approved-flag-${p.id}`}>
              <CheckCircle2 className="h-3.5 w-3.5" /> Approved{p.approved_by ? ` by ${p.approved_by}` : ""} — ready to send
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button disabled={ploading} onClick={openPreview} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-cool/40 text-cool hover:bg-cool/5" data-testid={`preview-${p.id}`}>
              {ploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Preview email
            </button>
            <button disabled={!!busy} onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`edit-${p.id}`}>
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
            {p.status === "approved" ? (
              <button disabled={!!busy} onClick={() => act(p.id, "s", () => sendProspectOutreach(p.id), (r) => r.ok ? "Outreach sent ✓" : (r.result?.reason || "Queued"))} className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 shadow-sm" data-testid={`send-${p.id}`}>
                {busy === p.id + "s" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send now
              </button>
            ) : (
              <>
                <button disabled={!!busy} onClick={() => act(p.id, "a", () => approveProspect(p.id), "Approved — now click Send now to email it")} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid={`approve-${p.id}`}>
                  {busy === p.id + "a" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Approve
                </button>
                <button disabled={!!busy} onClick={() => act(p.id, "s", () => sendProspectOutreach(p.id), (r) => r.ok ? "Outreach sent ✓" : (r.result?.reason || "Queued"))} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-green-400 text-green-700 hover:bg-green-50" data-testid={`approve-send-${p.id}`}>
                  {busy === p.id + "s" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Approve &amp; Send
                </button>
              </>
            )}
            <button disabled={!!busy} onClick={() => act(p.id, "x", () => rejectProspect(p.id), "Rejected")} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-300 text-slate-500 hover:bg-slate-50" data-testid={`approval-reject-${p.id}`}>
              <XCircle className="h-3.5 w-3.5" /> Reject
            </button>
          </div>
        </>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPreview(null)} data-testid={`preview-modal-${p.id}`}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Email preview — how the recipient sees it</p>
                <p className="text-sm font-semibold text-navy truncate">To: {preview.to || "no email"} · Subject: {preview.subject || "(none)"}</p>
              </div>
              <button onClick={() => setPreview(null)} className="text-slate-400 hover:text-slate-700 shrink-0" data-testid={`preview-close-${p.id}`}><XCircle className="h-5 w-5" /></button>
            </div>
            <iframe title="email-preview" srcDoc={preview.html} className="flex-1 w-full bg-white" style={{ minHeight: "60vh" }} />
          </div>
        </div>
      )}
    </div>
  );
}

function RoiCenter() {
  const [period, setPeriod] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await prospectRoi({ period })); } catch { toast.error("Could not load ROI"); }
    finally { setLoading(false); }
  }, [period]);
  useEffect(() => { load(); }, [load]);
  if (loading || !data) return <Loader2 className="h-6 w-6 animate-spin text-navy" />;
  const f = data.prospect_funnel, t = data.totals;
  const money = (n) => `$${Number(n || 0).toLocaleString()}`;
  const funnel = [
    { label: "Prospects", value: f.discovered }, { label: "Qualified", value: f.qualified },
    { label: "Leads", value: t.leads }, { label: "Appointments", value: t.appointments },
    { label: "Sold", value: t.sold }, { label: "Revenue", value: money(t.revenue) },
    { label: "Gross Profit", value: money(t.gross_profit) }, { label: "Acq. Cost", value: money(t.acquisition_cost) },
    { label: "ROI", value: t.roi == null ? "—" : `${t.roi}%` },
  ];
  return (
    <div data-testid="roi-center">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-slate-500">Where did Aquila make money? (gross profit @ {(data.gross_margin_pct * 100).toFixed(0)}% margin)</p>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm" data-testid="roi-period">
          <option value="week">This week</option><option value="month">This month</option><option value="ytd">Year to date</option><option value="all">All time</option>
        </select>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-9 gap-2 mb-6">
        {funnel.map((c) => (<div key={c.label} className="bg-white rounded-xl border border-slate-200 p-3 text-center"><p className="text-lg font-bold text-navy">{c.value}</p><p className="text-[10px] uppercase tracking-wide text-slate-400 mt-1">{c.label}</p></div>))}
      </div>
      <RoiTable title="By Source" rows={data.by_source} keyName="source" money={money} />
      <RoiTable title="B2B vs Residential" rows={data.by_type} keyName="type" money={money} />
      <div className="bg-white rounded-xl border border-slate-200 p-4 mt-4">
        <p className="font-heading font-bold text-navy text-sm mb-2">Prospect pipeline by category</p>
        {data.by_category.length === 0 ? <p className="text-sm text-slate-400">No prospects yet.</p> :
          <div className="grid sm:grid-cols-2 gap-2">{data.by_category.map((c) => (<div key={c.category} className="flex justify-between text-sm border-b border-slate-100 py-1"><span className="text-slate-600">{c.category}</span><span className="text-slate-400">{c.prospects} prospects · {c.qualified} qualified · {c.converted} converted</span></div>))}</div>}
      </div>
    </div>
  );
}

const RoiTable = ({ title, rows, keyName, money }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 mt-4 overflow-x-auto" data-testid={`roi-table-${keyName}`}>
    <p className="font-heading font-bold text-navy text-sm mb-2">{title}</p>
    {rows.length === 0 ? <p className="text-sm text-slate-400">No revenue data in this range yet.</p> :
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[11px] uppercase text-slate-400">
          <th className="py-1 pr-3">{keyName}</th><th className="py-1 px-2">Leads</th><th className="py-1 px-2">Appts</th><th className="py-1 px-2">Sold</th><th className="py-1 px-2">Revenue</th><th className="py-1 px-2">Gross Profit</th><th className="py-1 px-2">Acq. Cost</th><th className="py-1 px-2">ROI</th></tr></thead>
        <tbody>{rows.map((r) => (<tr key={r[keyName]} className="border-t border-slate-100">
          <td className="py-1.5 pr-3 font-semibold text-navy">{r[keyName]}</td><td className="px-2">{r.leads}</td><td className="px-2">{r.appointments}</td><td className="px-2">{r.sold}</td><td className="px-2">{money(r.revenue)}</td><td className="px-2 text-emerald-700">{money(r.gross_profit)}</td><td className="px-2">{money(r.acquisition_cost)}</td><td className="px-2 font-semibold">{r.roi == null ? "—" : `${r.roi}%`}</td></tr>))}</tbody>
      </table>}
  </div>
);

const Empty = ({ text }) => (
  <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-400">{text}</div>
);
